import { useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import useAuth from '../../hooks/useAuth';
import axios from '../../api/axios';
import useRefreshToken from '../../hooks/useRefreshToken';
import { Alert, CircularProgress } from '@mui/material';
import 'bootstrap/dist/css/bootstrap.min.css';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import InputAdornment from '@mui/material/InputAdornment';
import AuthPageLayout from './AuthPageLayout';

const LOGIN_URL = '/Login/Login';

const Login = () => {
    const { t, i18n } = useTranslation();
    const { auth, setAuth, persist, setPersist } = useAuth();
    const refresh = useRefreshToken();

    const navigate = useNavigate();
    const location = useLocation();

    // Determine default landing page based on role
    const getDefaultLandingPage = (role) => {
        const userRole = role?.toLowerCase();
        if (userRole === 'admin') {
            return '/Home';
        } else if (userRole === 'user') {
            return '/MySubmissions';
        }
        return '/Home'; // fallback
    };

    const userRef = useRef();
    const errRef = useRef();

    const [user, setUser] = useState('');
    const [pwd, setPwd] = useState('');
    const [errMsg, setErrMsg] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [showPassword, setShowPassword] = useState(false);

    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleMouseDownPassword = (event) => event.preventDefault();

    useEffect(() => {
        let isMounted = true;
        const verifyRefreshToken = async () => {
            try {
                await refresh();
            } catch (err) {
                // handle refresh error if needed
            } finally {
                isMounted && setIsLoading(false);
            }
        };

        !auth?.accessToken && persist ? verifyRefreshToken() : setIsLoading(false);
        return () => isMounted = false;
    }, []);

    useEffect(() => {
        if (Object.keys(auth).length > 0) {
            // If there's a previous location to return to, use it (if user has access)
            // Otherwise, use role-based default landing page
            const from = location.state?.from?.pathname;
            const landingPage = from || getDefaultLandingPage(auth.role);
            navigate(landingPage, { replace: true });
        }
    }, [auth, navigate, location.state]);

    useEffect(() => {
        setErrMsg('');
    }, [user, pwd]);

    useEffect(() => {
        localStorage.setItem("persist", persist);
    }, [persist]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const response = await axios.post(
                LOGIN_URL,
                { username: user, password: pwd },
                {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true
                }
            );

            setAuth({
                user: response.data.username,
                fullName: response.data.fullName,
                email: response.data.email,
                role: response.data.role,
                accessToken: response.data.accessToken,
                userId: response.data.userId,
                components: response.data.components,
                universityId: response.data.universityId,
                universityName: response.data.universityName,
            });
            setUser('');
            setPwd('');
            i18n.changeLanguage(response?.data?.lang);
            // Navigate to role-based landing page
            const from = location.state?.from?.pathname;
            const landingPage = from || getDefaultLandingPage(response.data.role);
            navigate(landingPage, { replace: true });
        } catch (err) {
            if (!err?.response) {
                setErrMsg(t('errorNoServerResponse'));
            } else if (err.response?.status === 400) {
                setErrMsg(t('errorMissingCredentials'));
            } else if (err.response?.status === 401) {
                setErrMsg(t('errorUnauthorized'));
            } else if (err.response?.status === 403) {
                // Handle approval status errors
                const errorCode = err.response?.data?.error;
                if (errorCode === 'PENDING_APPROVAL') {
                    setErrMsg(t('errorPendingApproval'));
                } else if (errorCode === 'REGISTRATION_REJECTED') {
                    const reason = err.response?.data?.reason;
                    setErrMsg(reason ? `${t('errorRegistrationRejected')}: ${reason}` : t('errorRegistrationRejected'));
                } else {
                    setErrMsg(t('errorLoginFailed'));
                }
            } else {
                setErrMsg(t('errorLoginFailed'));
            }
            if (errRef.current) {
                errRef.current.focus();
            }
        }
    };

    const togglePersist = () => {
        setPersist(prev => !prev);
    };

    if (isLoading) {
        return (
            <AuthPageLayout>
                <CircularProgress />
            </AuthPageLayout>
        );
    }

    return (
        <AuthPageLayout>
            <div
                className="card shadow"
                style={{ width: '100%', maxWidth: '400px', border: 'none', borderRadius: '0.5rem' }}
            >
                <div className="card-body">
                    <div className="text-center mb-4">
                        <Link to="/Home">
                            <img
                                src="/assets/img/OnlineCADCOM_FullLogo.png"
                                alt="Online-CADCOM"
                                style={{ height: '60px' }}
                            />
                        </Link>
                    </div>

                    {errMsg && (
                        <Alert
                            ref={errRef}
                            variant="filled"
                            severity="error"
                            className="mb-3"
                            tabIndex={-1}
                            action={
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={() => setErrMsg('')}
                                >
                                    <CloseIcon fontSize="inherit" />
                                </IconButton>
                            }
                        >
                            {errMsg}
                        </Alert>
                    )}
                    <form onSubmit={handleSubmit}>
                        <div className="form-floating mb-3">
                            <input
                                type="text"
                                className="form-control"
                                id="floatingInput"
                                placeholder={t("loginUsername")}
                                ref={userRef}
                                onChange={(e) => setUser(e.target.value)}
                                value={user}
                                required
                                autoComplete="username"
                            />
                            <label htmlFor="floatingInput">{t("loginUsername")}</label>
                        </div>
                        <div className="form-floating mb-3 position-relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                className="form-control"
                                id="floatingPassword"
                                placeholder={t("loginPassword")}
                                onChange={(e) => setPwd(e.target.value)}
                                value={pwd}
                                required
                                autoComplete="current-password"
                                style={{ paddingRight: '45px' }}
                            />
                            <label htmlFor="floatingPassword">{t("loginPassword")}</label>
                            <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                edge="end"
                                size="small"
                                style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    zIndex: 2
                                }}
                            >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </div>
                        <button
                            className="btn btn-lg w-100"
                            type="submit"
                            style={{ backgroundColor: '#112d4e', color: '#fff', border: 'none' }}
                        >
                            {t("loginSubmitButton")}
                        </button>
                    </form>
                    <div className="text-center mt-3">
                        <p className="mb-2">
                            <Link to="/forgot-password" style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                                {t("loginForgotPassword")}
                            </Link>
                        </p>
                        <p className="mb-0">
                            {t("loginNoAccount")}{' '}
                            <Link to="/register" style={{ color: '#112d4e', fontWeight: 'bold' }}>
                                {t("loginRegisterLink")}
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </AuthPageLayout>
    );
};
export default Login;
